import {DestroyRef, inject, Injectable, isDevMode, PLATFORM_ID} from '@angular/core';
import {DOCUMENT, isPlatformBrowser} from '@angular/common';
import {Router} from '@angular/router';
import {takeUntilDestroyed} from '@angular/core/rxjs-interop';
import {webSocket, WebSocketSubject, WebSocketSubjectConfig} from 'rxjs/webSocket';
import {EMPTY, fromEvent, merge, Subscription, timer} from 'rxjs';
import {catchError, retry} from 'rxjs/operators';
import {BillResponse} from '@core/models';
import {SessionStore} from '@store/session.store';

export interface TrackingData extends BillResponse {
  status?: string;
  message?: string;
  billResponse?: any;
}

@Injectable({
  providedIn: 'root'
})
export class TrackingWebsocket {
  private readonly sessionStore = inject(SessionStore);
  private readonly router = inject(Router);
  private readonly document = inject(DOCUMENT);
  private readonly destroyRef = inject(DestroyRef);
  private readonly platformId = inject(PLATFORM_ID);

  private socket$: WebSocketSubject<TrackingData> | null = null;
  private connectionSub?: Subscription;

  private isConnectingOrConnected = false;
  private intentionalClose = false;
  private reconnectAttempts = 0;
  private readonly MAX_RECONNECT_DELAY = 10000;

  constructor() {

    if (isPlatformBrowser(this.platformId)) {
      this.initVisibilityAndNetworkListeners();
      this.initPageLifecycleListeners();
    }

    this.destroyRef.onDestroy(() => {
      this.close();
    });
  }

  public connect(orderId: string, customerId: string): void {
    if (!isPlatformBrowser(this.platformId)) return;

    sessionStorage.setItem('current_order_id', orderId);
    sessionStorage.setItem('current_customer_id', customerId);

    if (this.isConnectingOrConnected && this.socket$) {
      this.sessionStore.loadOrderStatusSession();
      return;
    }

    this.close();

    this.isConnectingOrConnected = true;
    this.intentionalClose = false;

    const url = this.buildUrl(orderId, customerId);
    this.socket$ = this.getNewWebSocket(url);

    this.connectionSub = this.socket$.pipe(
      retry({
        delay: (error, retryCount) => {
          this.reconnectAttempts = retryCount;
          const delayTime = Math.min(Math.pow(2, retryCount) * 1000, this.MAX_RECONNECT_DELAY);
          console.warn(`Reconectando WS (Intento ${retryCount}) en ${delayTime}ms...`);
          return timer(delayTime);
        }
      }),
      catchError(() => {
        this.isConnectingOrConnected = false;
        return EMPTY;
      }),
      takeUntilDestroyed(this.destroyRef)
    ).subscribe({
      next: (data: TrackingData) => {
        this.reconnectAttempts = 0;

        if (data && Object.keys(data).length > 0 && Object.prototype.hasOwnProperty.call(data, 'status')) {
          sessionStorage.removeItem('current_order_id');
          sessionStorage.removeItem('current_customer_id');
          this.router.navigate(['/standard/success'], {replaceUrl: true});

          this.sessionStore.setOderRestaurant(data.billResponse as BillResponse);
          this.intentionalClose = true;
          this.close();
          return;
        }

        if (data && Object.keys(data).length > 0) {
          this.sessionStore.setOderRestaurant(data as BillResponse);
        }
      },
      error: (err) => {
        console.error('Error en canal WebSocket:', err);
        this.isConnectingOrConnected = false;
      },
      complete: () => {
        this.isConnectingOrConnected = false;


        if (!this.intentionalClose) {
          const currentOrderId = sessionStorage.getItem('current_order_id');
          const currentCustomerId = sessionStorage.getItem('current_customer_id');

          if (currentOrderId && currentCustomerId) {
            this.socket$ = null;


            timer(3000)
              .pipe(takeUntilDestroyed(this.destroyRef))
              .subscribe(() => {
                this.connect(currentOrderId, currentCustomerId);
              });
          }
        }
      }
    });
  }

  public close(): void {
    this.intentionalClose = true;
    this.isConnectingOrConnected = false;

    if (this.connectionSub) {
      this.connectionSub.unsubscribe();
      this.connectionSub = undefined;
    }

    if (this.socket$) {
      this.socket$.complete();
      this.socket$ = null;
    }
  }

  private initVisibilityAndNetworkListeners(): void {
    const windowObj = this.document.defaultView;
    if (!windowObj) return;


    merge(
      fromEvent(this.document, 'visibilitychange'),
      fromEvent(windowObj, 'online')
    )
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((event) => {
        const isVisible = this.document.visibilityState === 'visible';
        const isOnline = windowObj.navigator.onLine;


        if (isVisible && isOnline) {
          const orderId = sessionStorage.getItem('current_order_id');
          const customerId = sessionStorage.getItem('current_customer_id');


          if (orderId && customerId && !this.isConnectingOrConnected) {

            this.connect(orderId, customerId);
          }
        }
      });
  }

  private initPageLifecycleListeners(): void {
    const windowObj = this.document.defaultView;
    if (!windowObj) return;

    fromEvent<PageTransitionEvent>(windowObj, 'pageshow')
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((event) => {
        if (event.persisted) {
          this.isConnectingOrConnected = false;
          const orderId = sessionStorage.getItem('current_order_id');
          const customerId = sessionStorage.getItem('current_customer_id');
          if (orderId && customerId) {
            this.connect(orderId, customerId);
          }
        }
      });

    fromEvent<PageTransitionEvent>(windowObj, 'pagehide')
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        const orderId = sessionStorage.getItem('current_order_id');
        if (orderId) {
          this.close();
        }
      });
  }

  private getNewWebSocket(url: string): WebSocketSubject<TrackingData> {
    const config: WebSocketSubjectConfig<TrackingData> = {
      url: url,
      openObserver: {
        next: () => {
          this.reconnectAttempts = 0;
          this.sessionStore.loadOrderStatusSession();
        },
      },
      deserializer: (e) => {
        try {
          return JSON.parse(e.data);
        } catch (error) {
          return {} as TrackingData;
        }
      }
    };
    return webSocket(config);
  }

  private buildUrl(orderId: string, customerId: string): string {
    const protocol = window.location.protocol === 'https:' ? 'wss' : 'ws';
    const host = isDevMode() ? 'localhost:9018' : window.location.host;
    return `${protocol}://${host}/ws/tracking/order/${orderId}/customer/${customerId}`;
  }
}
